<template>
    <div class="quickpay-payment-stripe">
        <div class="quickpay-payment-stripe__container">
            <form id="payment-form">
                <CompassAlert
                    v-if="errorMessage"
                    type="error"
                    :description="errorMessage"
                    :showClose="false"
                />
                <div id="payment-element"></div>
                <CompassButton
                    v-if="isPaymentElementReady"
                    id="submit"
                    :label="payButtonLabel"
                    size="large"
                    :loading="isProcessingPayment"
                    submit
                    class="quickpay-payment-stripe__button"
                />
            </form>
        </div>
    </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';

export default {
    props: {
        hash: {
            type: String,
            required: true,
        },
        deadlineId: {
            type: Number,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            translations: {
                pay: this.$t.__('common.labels.pay'),
            },
            dataStripe: null,
            errorMessage: '',
            isPaymentElementReady: false,
            isProcessingPayment: false,
        };
    },
    computed: {
        userLocale() {
            const locale = window.App?.locale || 'fr_FR';
            return locale.split('.')[0].replace('_', '-');
        },
        payButtonLabel() {
            if (!this.dataStripe || !this.dataStripe.amount) {
                return this.translations.pay;
            }
            const amount = parseFloat(this.dataStripe.amount) / 100;
            const currency = this.dataStripe.currency || 'EUR';
            let locale = this.dataStripe.locale || 'fr-FR';
            // Stripe can return "auto", which is not a valid locale for Intl
            if (locale === 'auto') {
                locale = this.userLocale;
            }
            const formattedAmount = new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(amount);

            return `${this.translations.pay} ${formattedAmount}`;
        },
    },
    async created() {
        await this.fetchStripeData();

        if (this.dataStripe.status === 'succeeded') {
            return;
        }

        await this.initializeStripe();
    },
    methods: {
        async fetchStripeData() {
            const params = new URLSearchParams();
            params.append('hash', this.hash);
            params.append('deadlineid', this.deadlineId);
            const response = await this.$ajax.post('/stripe/checkout/request', {}, params);
            this.dataStripe = response.data.data;
        },
        async initializeStripe() {
            const stripe = await loadStripe(this.dataStripe.key);
            const elements = stripe.elements({
                clientSecret: this.dataStripe.clientSecret,
                appearance: { theme: 'flat' },
            });
            const paymentElement = elements.create('payment', {
                layout: 'tabs',
                defaultCollapsed: false,
            });
            paymentElement.mount('#payment-element');
            paymentElement.on('ready', () => {
                this.isPaymentElementReady = true;
            });
            this.setupPaymentForm(stripe, elements);
        },
        setupPaymentForm(stripe, elements) {
            const form = document.getElementById('payment-form');
            form.addEventListener('submit', async (event) => {
                event.preventDefault();
                this.isProcessingPayment = true;
                const { error } = await stripe.confirmPayment({
                    elements,
                    confirmParams: {
                        return_url: this.buildReturnUrl(),
                    },
                });
                if (error) {
                    this.isProcessingPayment = false;
                    this.displayError(error.message);
                }
            });
        },
        buildReturnUrl() {
            const params = new URLSearchParams({
                v2: '',
                hash: this.dataStripe.hash, // use request hash for return URL
            });
            if (this.deadlineId) {
                params.append('deadlineid', this.deadlineId);
            }

            return `${this.dataStripe.returnUrl}?${params.toString()}`;
        },
        displayError(message) {
            this.errorMessage = message;
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-payment-stripe {
    &__container {
        background: white;
        border-radius: var(--spacing-1);
        padding: var(--spacing-6);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

        h2 {
            margin-bottom: var(--spacing-3);
        }

        p {
            margin-bottom: var(--spacing-5);
            color: var(--color-text-secondary);
        }
    }

    &__button {
        width: 100%;
        margin: var(--spacing-3) 0 0;
    }
}
</style>
